import React, { useCallback, useEffect, useState } from 'react';
import { Form, Input, Modal, message } from 'antd';
import { Button } from 'shared/components/Button';
import { unwrapResult } from '@reduxjs/toolkit';
import { useUpdatingRef } from 'shared/hooks';
import { changeActiveOrg, createOrg } from 'shared/store/shared-actions';
import { useAppDispatch } from 'shared/store';
import './style.less';

interface CreateOrgModalProps {
  isVisible: boolean;
  onClose(): void;
}

export const CreateOrgModal: React.FC<CreateOrgModalProps> = ({ isVisible, onClose }) => {
  const dispatch = useAppDispatch();
  const [form] = Form.useForm();
  const formRef = useUpdatingRef(form);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleFormSubmit = useCallback(
    async values => {
      if (!values.name) {
        return;
      }
      setIsSubmitting(true);
      try {
        const res = await dispatch(createOrg(values));
        const newUser = unwrapResult(res);
        dispatch(changeActiveOrg(newUser.org.id));
      } catch (err) {
        message.error(err.message || 'Failed to create new organization');
      }
      setIsSubmitting(false);
    },
    [dispatch],
  );

  // Reset form when opened
  useEffect(() => {
    if (isVisible) {
      formRef.current.resetFields();
    }
  }, [isVisible, formRef]);

  return (
    <Modal className="CreateOrgModal" visible={isVisible} onCancel={onClose}>
      <h2 className="CreateOrgModal__title">Create new organization</h2>
      <Form
        className="CreateOrgModal__form"
        form={form}
        onFinish={handleFormSubmit}
        layout="vertical"
        requiredMark={false}
      >
        <div className="CreateOrgModal__form-field">
          <Form.Item name="name" label="Organization name" required>
            <Input name="name" autoFocus required />
          </Form.Item>
          <div className="CreateOrgModal__form-field-required" />
        </div>
        <div className="CreateOrgModal__form-buttons">
          <Button
            type="primary"
            size="large"
            className="CreateOrgModal__form-buttons-submit"
            htmlType="submit"
            loading={isSubmitting}
          >
            Create organization
          </Button>
          <Button type="text" onClick={onClose} disabled={isSubmitting}>
            Cancel
          </Button>
        </div>
      </Form>
    </Modal>
  );
};
